/**
 * RESPUESTAS SIMULADAS DE IA.
 *
 * Nada de este archivo llama a una IA. Son textos de ejemplo que reproducen el flujo aprobado
 * (borrador → fuentes → incierto → aprobar/editar/descartar). Se sustituirá por llamadas al
 * servidor, que comprobará los permisos antes de enviar nada a la IA.
 */
import type { BloqueIA, ElementoBiblioteca, RegistroDiario, Sueno } from '../modelo/tipos';

export type AccionBiblioteca = 'resumen' | 'ideas' | 'capitulos' | 'profundizar';
export type Extension = 'Breve' | 'Extenso';

export const ETIQUETAS_ACCION: Record<AccionBiblioteca, string> = {
  resumen: 'Crear resumen',
  ideas: 'Extraer ideas principales',
  capitulos: 'Resumir por capítulos',
  profundizar: 'Profundizar en un concepto'
};

export interface BorradorBiblioteca {
  texto: string;
  fuentes: string;
  incierto: string;
}

const SIGNIFICADOS: Record<string, string> = {
  Agua: 'el agua se asocia a menudo con lo emocional y con lo que fluye',
  Vuelo: 'volar suele relacionarse con libertad o con tomar distancia',
  Familia: 'la familia suele aludir a vínculos y pertenencia',
  Naturaleza: 'la naturaleza puede evocar lo vital y lo que crece a su ritmo',
  Cambio: 'el cambio puede hablar de una transición en curso'
};

export function borradorSueno(sueno: Sueno): string {
  const partes = sueno.simbolos.map((s) => SIGNIFICADOS[s]).filter(Boolean);
  let txt = 'Esto es una posible lectura, no una verdad.\n\n';
  txt += partes.length
    ? 'En muchas tradiciones, ' + partes.join('; ') + '. '
    : 'No etiquetaste símbolos, así que me baso solo en el relato. ';
  txt += 'Estos significados son generales: lo que el símbolo signifique para ti pesa más que cualquier tradición.\n\nPregunta para ti: ¿qué sentías justo antes de despertar?';
  return txt;
}

export function borradorBiblioteca(
  el: ElementoBiblioteca,
  accion: AccionBiblioteca,
  extension: Extension,
  concepto: string
): BorradorBiblioteca {
  if (accion === 'profundizar') {
    return {
      texto: 'Concepto: «' + (concepto || 'sin indicar') + '».\n\nRespuesta de demostración: aquí la IA desarrollaría el concepto usando solo «' + el.descripcionMaterial + '», citando dónde aparece. Si el concepto no aparece en el material, te lo diría en lugar de inventarlo.',
      fuentes: el.descripcionMaterial,
      incierto: ''
    };
  }
  if (el.tipoMaterial === 'transcripcion') {
    if (accion === 'resumen' && extension === 'Breve') return { texto: 'El episodio propone que escuchar a otros empieza por escucharse: notar la propia reacción antes de responder. Presenta la pausa como una habilidad que se entrena y sugiere pequeñas prácticas diarias para ensayarla.', fuentes: 'Transcripción, minutos 4 a 18.', incierto: 'Entre los minutos 22 y 24 la transcripción está incompleta; no la he usado.' };
    if (accion === 'resumen') return { texto: 'El episodio parte de una idea sencilla: no podemos escuchar bien a otros si no registramos lo que nos pasa mientras escuchamos.\n\nEn la parte central se describe la pausa como un hábito que se entrena: respirar antes de responder, nombrar la emoción del momento y elegir la respuesta en lugar de reaccionar.\n\nAl final se proponen tres prácticas breves para la vida diaria y se reconoce que al principio resultan incómodas.', fuentes: 'Transcripción completa disponible (minutos 0 a 31), salvo el tramo indicado abajo.', incierto: 'Entre los minutos 22 y 24 la transcripción está incompleta; no la he usado.' };
    if (accion === 'ideas') return { texto: '1. Escuchar a otros requiere registrar primero la propia reacción.\n2. La pausa antes de responder se entrena con repetición.\n3. Nombrar lo que se siente reduce la urgencia de reaccionar.', fuentes: 'Transcripción, minutos 4 a 29.', incierto: '' };
    return { texto: 'Este material es un único episodio sin capítulos. Si quieres, puedo dividir el resumen por bloques de la transcripción.', fuentes: 'Estructura de la transcripción.', incierto: '' };
  }
  if (accion === 'capitulos') return { texto: 'Capítulo 1 · El punto de partida: por qué hacer un mapa de uno mismo.\nCapítulo 2 · Territorios: emociones, vínculos y creencias.\nCapítulo 3 · Los cambios de escala: cómo varía el mapa con el tiempo.\nCapítulo 4 · Herramientas de registro: diarios, sueños y símbolos.\nCapítulos 5 a 9 · No disponibles en tu material: no los resumo.', fuentes: 'Tus apuntes en PDF, capítulos 1 a 4.', incierto: 'Faltan los capítulos 5 a 9. No los resumo ni los deduzco.' };
  if (accion === 'ideas') return { texto: '1. El autoconocimiento se plantea como un mapa que se redibuja.\n2. Registrar experiencias permite ver cambios que no se notan en el día a día.\n3. Los símbolos personales importan más que los significados universales.', fuentes: 'Tus apuntes en PDF, capítulos 1 a 4.', incierto: 'Solo cubre los capítulos 1 a 4 de 9.' };
  if (extension === 'Breve') return { texto: 'Los capítulos disponibles presentan el autoconocimiento como un mapa personal que cambia con el tiempo, y proponen el registro de emociones, sueños y símbolos como forma de dibujarlo.', fuentes: 'Tus apuntes en PDF, capítulos 1 a 4.', incierto: 'Solo tengo los capítulos 1 a 4 de 9: no es un resumen del libro completo.' };
  return { texto: 'Los cuatro primeros capítulos construyen una metáfora: conocerse es cartografiar un territorio propio.\n\nEl capítulo 2 describe ese territorio (emociones, vínculos, creencias) y el 3 insiste en que el mapa cambia de escala con el tiempo.\n\nEl capítulo 4 propone herramientas concretas de registro, con especial atención a los sueños y los símbolos personales.', fuentes: 'Tus apuntes en PDF, capítulos 1 a 4.', incierto: 'Solo tengo los capítulos 1 a 4 de 9: no es un resumen del libro completo.' };
}

/**
 * Observación de patrones del diario. El recuento es real (solo sobre registros con permiso);
 * el texto que lo envuelve es una plantilla.
 */
export function observarPatrones(registros: RegistroDiario[]): string {
  const autorizados = registros.filter((r) => r.permitirIA);
  if (!autorizados.length) {
    return 'No hay registros con permiso de IA, así que no puedo observar nada. No he leído ningún registro.';
  }
  const cuenta: Record<string, number> = {};
  autorizados.forEach((r) => r.emociones.forEach((e) => { cuenta[e] = (cuenta[e] || 0) + 1; }));
  const frecuentes = Object.keys(cuenta)
    .sort((a, b) => cuenta[b] - cuenta[a])
    .map((k) => k + ' (' + cuenta[k] + ')')
    .join(', ');
  const conIntensidad = autorizados.filter((r) => r.intensidad);
  const media = conIntensidad.length
    ? (conIntensidad.reduce((s, r) => s + r.intensidad, 0) / conIntensidad.length).toFixed(1).replace('.', ',')
    : null;
  return (
    'He usado ' + autorizados.length + ' de ' + registros.length + ' registros: solo los que tienen permiso. Los demás no se han leído.\n\n' +
    'Emociones que aparecen: ' + (frecuentes || 'ninguna') + '.' + (media ? ' Intensidad media: ' + media + ' sobre 5.' : '') +
    '\n\nCon tan pocos datos esto es una observación, no una tendencia, y no indica causas. ¿Te resuena?'
  );
}

export const NOMBRES_CONTEXTO = { suenos: 'Sueños', biblioteca: 'Biblioteca', diario: 'Diario', cursos: 'Cursos' } as const;
export type ClaveContexto = keyof typeof NOMBRES_CONTEXTO;

export function respuestaConversacion(contexto: Record<ClaveContexto, boolean>): BloqueIA[] {
  const claves = Object.keys(NOMBRES_CONTEXTO) as ClaveContexto[];
  const activos = claves.filter((k) => contexto[k]);
  const fuera = claves.filter((k) => !contexto[k]).map((k) => NOMBRES_CONTEXTO[k]);
  const bloques: BloqueIA[] = [];
  if (activos.length) {
    bloques.push({ tipo: 'cita', etiqueta: 'Dónde buscaría', texto: 'Respuesta de demostración: consultaría solo ' + activos.map((k) => NOMBRES_CONTEXTO[k]).join(', ') + ', y dentro de ellos solo los registros con permiso. Citaría cada registro que use.' });
    bloques.push({ tipo: 'interp', etiqueta: 'Interpreto (hipótesis)', texto: 'Aquí iría una posible conexión, marcada siempre como hipótesis.' });
  }
  bloques.push({
    tipo: 'falta',
    etiqueta: 'Me faltan datos',
    texto: (activos.length ? '' : 'No hay contexto elegido: respondería sin consultar tus registros. ') + (fuera.length ? 'No he consultado: ' + fuera.join(', ') + '. ' : '') + 'Terapia queda siempre fuera.'
  });
  return bloques;
}
